import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { toast } from 'sonner';
import { Copy, Eye, EyeOff } from 'lucide-react';
import type { CognitoUser, RegenerateTempPasswordResponse } from '@metron/shared';
import { createLogger } from '@metron/shared';

const logger = createLogger('GenerateTempPasswordModal');

interface GenerateTempPasswordModalProps {
  open: boolean;
  user: CognitoUser | null;
  onOpenChange: (open: boolean) => void;
  onGenerate: (userId: string) => Promise<RegenerateTempPasswordResponse>;
}

type ModalStep = 'confirm' | 'result';

export function GenerateTempPasswordModal({
  open,
  user,
  onOpenChange,
  onGenerate,
}: GenerateTempPasswordModalProps) {
  const [step, setStep] = useState<ModalStep>('confirm');
  const [isLoading, setIsLoading] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [resultEmail, setResultEmail] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  // Reset to confirm state and drop password whenever the modal is closed
  useEffect(() => {
    if (!open) {
      setStep('confirm');
      setTemporaryPassword(null);
      setResultEmail(null);
      setRevealed(false);
      setIsLoading(false);
    }
  }, [open]);

  const handleGenerate = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const response = await onGenerate(user.username);
      setTemporaryPassword(response.temporaryPassword);
      setResultEmail(response.email);
      setStep('result');
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to generate temporary password';
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to generate temporary password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!temporaryPassword) return;
    await navigator.clipboard.writeText(temporaryPassword);
    toast.success('Password copied to clipboard');
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        {step === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle>Generate Temporary Password</DialogTitle>
              <DialogDescription>
                Generate a temporary password for {user.name} ({user.email})?
              </DialogDescription>
            </DialogHeader>
            <div className='py-4'>
              <p className='text-sm text-muted-foreground'>
                This will invalidate the user's current password and force them to set a new one on
                next sign-in.
              </p>
            </div>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={isLoading}>
                {isLoading ? 'Generating...' : 'Generate temporary password'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'result' && temporaryPassword !== null && (
          <>
            <DialogHeader>
              <DialogTitle>Temporary Password Generated</DialogTitle>
              <DialogDescription>
                Login email: <span className='font-medium text-foreground'>{resultEmail}</span>
              </DialogDescription>
            </DialogHeader>
            <div className='py-4 space-y-4'>
              <div className='flex items-center gap-2'>
                <Input
                  type={revealed ? 'text' : 'password'}
                  readOnly
                  value={temporaryPassword}
                  className='font-mono'
                  aria-label='Temporary password'
                />
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  aria-label={revealed ? 'Hide password' : 'Reveal password'}
                  onClick={() => setRevealed((prev) => !prev)}
                >
                  {revealed ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  aria-label='Copy'
                  onClick={handleCopy}
                >
                  <Copy className='h-4 w-4' />
                </Button>
              </div>
              <p className='text-sm text-amber-600 dark:text-amber-400'>
                This password is shown only once. Copy it now and deliver it to the customer through
                a secure channel.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
